import {
  type ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { membershipSchema } from "../schemas";
import { Memberships } from "../../../types/Types";
import { previewWalletPricing } from "../../../api/mutations/usePreviewWalletPricing";
import { getPaymentErrorMessage } from "../../../utils/paymentErrorLogger";
import {
  isEarlyStageMembership,
  isSameMembershipPlan,
  sortMembershipsForRegistration,
} from "../../../utils/membershipTier";
import { syncRegistrationMembership } from "../registrationMembershipSync";

const CONTAINER_MIN_HEIGHT = 400;
const CONTAINER_STYLE = { minHeight: CONTAINER_MIN_HEIGHT };
const CARD_CLICKABLE_STYLE = { cursor: "pointer" as const };
const STRIKETHROUGH_STYLE = {
  textDecoration: "line-through" as const,
  marginRight: "8px",
  color: "grey",
};

type PreviewEntry =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok"; major: number }
  | { status: "error"; message: string };

interface MembershipData {
  membershipName: string;
  isAnnualMembership: number;
}

interface MembershipStepProps {
  defaultValues: MembershipData;
  memberships: Memberships[];
  onNext: (data: MembershipData, price: number) => void;
  onBack: () => void;
  setMembershipPrice: (price: number) => void;
  discountCode: string;
  setDiscountCode: (code: string) => void;
  /** Required to validate membership promotion codes against Stripe (preview-wallet-pricing). */
  previewUserId?: string;
  previewEmail?: string;
  previewName?: string;
  /** When upgrading/renewing, pass current user membership so that option is disabled */
  currentMembershipName?: string;
  /** Explorer combo / fixed-tier signup: only this plan can be selected */
  lockedMembershipName?: string;
  /**
   * Persist the selected plan to the user row before Stripe promo preview runs.
   * Required when the DB still has another tier (e.g. Early Stage) during registration.
   */
  syncMembershipSelection?: boolean;
  /** Early Stage: open under-23 Young Professional verification path. */
  onEarlyStageYoungProfessional?: (membershipName: string) => void;
  /** Hide under-23 link after user proceeds on the standard £10 path. */
  showEarlyStageYoungProfessionalLink?: boolean;
}

function minorToMajor(amountMinor: number, currency: string): number {
  const c = currency.trim().toLowerCase();
  if (
    c === "jpy" ||
    c === "vnd" ||
    c === "xaf" ||
    c === "xpf" ||
    c === "clp" ||
    c === "ugx"
  ) {
    return amountMinor;
  }
  return amountMinor / 100;
}

function listPriceMajor(m: Memberships, isAnnual: boolean): number {
  return isAnnual ? Number(m.annualFee) : Number(m.monthlyFee);
}

function productIdFor(m: Memberships, isAnnual: boolean): string {
  return (isAnnual ? m.productIdAnnual : m.productId) || "";
}

function membershipPlanLabelKey(
  membershipName: string,
):
  | "membership.plan.youth"
  | "membership.plan.business"
  | "membership.plan.executive"
  | null {
  if (membershipName === "Youth Membership") return "membership.plan.youth";
  if (membershipName === "Business Membership")
    return "membership.plan.business";
  if (membershipName === "Executive Membership")
    return "membership.plan.executive";
  return null;
}

function MembershipStepInner({
  defaultValues,
  memberships,
  setMembershipPrice,
  onNext,
  onBack,
  discountCode,
  setDiscountCode,
  currentMembershipName,
  lockedMembershipName,
  syncMembershipSelection = false,
  onEarlyStageYoungProfessional,
  showEarlyStageYoungProfessionalLink = true,
  previewUserId,
  previewEmail,
  previewName,
}: MembershipStepProps) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<MembershipData>({
    resolver: yupResolver(membershipSchema),
    defaultValues,
  });

  const [discountError, setDiscountError] = useState("");
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [previewByProductId, setPreviewByProductId] = useState<
    Record<string, PreviewEntry>
  >({});
  const [previewRunning, setPreviewRunning] = useState(false);
  const [membershipSelectionSaving, setMembershipSelectionSaving] =
    useState(false);

  // Sync from parent only when values actually change (e.g. back navigation).
  // Do not depend on `defaultValues` object identity — parents pass a new
  // inline object on every re-render (e.g. discountCode keystrokes), which
  // would wipe local billing-cycle / plan selection.
  const defaultMembershipName = defaultValues.membershipName;
  const defaultIsAnnualMembership = defaultValues.isAnnualMembership;
  useEffect(() => {
    reset({
      membershipName: defaultMembershipName,
      isAnnualMembership: defaultIsAnnualMembership,
    });
  }, [defaultMembershipName, defaultIsAnnualMembership, reset]);

  const membershipName = watch("membershipName");
  const isAnnualMembership = watch("isAnnualMembership");
  const isAnnual = isAnnualMembership === 1;

  const sortedMemberships = useMemo(
    () => sortMembershipsForRegistration(memberships),
    [memberships],
  );

  const setBillingCycle = useCallback(
    (annual: boolean) => {
      setValue("isAnnualMembership", annual ? 1 : 0);
      setDiscountError("");
      if (annual && isEarlyStageMembership(membershipName)) {
        const next = sortedMemberships.find(
          (m) => !isEarlyStageMembership(m.name),
        );
        if (next) setValue("membershipName", next.name);
      }
    },
    [setValue, membershipName, sortedMemberships],
  );

  // Locked tier (e.g. Explorer combo) or first selectable plan on upgrade
  useEffect(() => {
    if (sortedMemberships.length === 0) return;

    if (lockedMembershipName?.trim()) {
      const locked = sortedMemberships.find((m) =>
        isSameMembershipPlan(m.name, lockedMembershipName),
      );
      if (locked && membershipName !== locked.name) {
        setValue("membershipName", locked.name);
      }
      return;
    }

    const firstSelectable = sortedMemberships.find(
      (m) =>
        !currentMembershipName ||
        !isSameMembershipPlan(m.name, currentMembershipName),
    );
    if (!firstSelectable) return;

    const knownName = sortedMemberships.some((m) => m.name === membershipName);
    const onDisabledPlan =
      !!currentMembershipName &&
      isSameMembershipPlan(membershipName, currentMembershipName);

    if (!knownName || onDisabledPlan) {
      setValue("membershipName", firstSelectable.name);
    }
  }, [
    sortedMemberships,
    currentMembershipName,
    lockedMembershipName,
    membershipName,
    setValue,
  ]);

  const codeTrimmed = discountCode.trim();

  const selectedMembershipRow = useMemo(
    () => sortedMemberships.find((m) => m.name === membershipName),
    [sortedMemberships, membershipName],
  );
  const selectedProductId = selectedMembershipRow
    ? productIdFor(selectedMembershipRow, isAnnual)
    : "";

  /** One preview call per debounced change — selected Stripe product only (not every tier). */
  useEffect(() => {
    if (!codeTrimmed || !previewUserId?.trim() || !selectedProductId) {
      setPreviewByProductId({});
      setPreviewRunning(false);
      return;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      void (async () => {
        setPreviewRunning(true);
        setPreviewByProductId({
          [selectedProductId]: { status: "loading" },
        });

        try {
          const res = await previewWalletPricing({
            userId: previewUserId.trim(),
            productId: selectedProductId,
            discountCode: codeTrimmed,
            isAnnual,
            isEvent: false,
            isEventEarlyBird: false,
            name: previewName,
            email: previewEmail,
          });
          if (ac.signal.aborted) return;
          const major = minorToMajor(res.amountMinor, res.currency);
          setPreviewByProductId({
            [selectedProductId]: { status: "ok", major },
          });
        } catch (e: unknown) {
          if (ac.signal.aborted) return;
          setPreviewByProductId({
            [selectedProductId]: {
              status: "error",
              message: getPaymentErrorMessage(e),
            },
          });
        } finally {
          if (!ac.signal.aborted) setPreviewRunning(false);
        }
      })();
    }, 450);

    return () => {
      ac.abort();
      clearTimeout(timer);
    };
  }, [
    codeTrimmed,
    previewUserId,
    previewEmail,
    previewName,
    selectedProductId,
    isAnnual,
  ]);

  const handleDiscountChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setDiscountCode(e.target.value);
      setDiscountError("");
    },
    [setDiscountCode],
  );

  const selectedPreview = selectedProductId
    ? previewByProductId[selectedProductId]
    : undefined;

  /** Same pattern as event Stripe promo: show API/Stripe message under the field when preview fails. */
  const membershipStripeFieldError =
    codeTrimmed &&
    previewUserId?.trim() &&
    !previewRunning &&
    selectedPreview?.status === "error"
      ? selectedPreview.message
      : "";

  const displayedMajor = useCallback(
    (m: Memberships) => {
      const list = listPriceMajor(m, isAnnual);
      const pid = productIdFor(m, isAnnual);
      if (!codeTrimmed || !previewUserId?.trim()) return list;
      const p = previewByProductId[pid];
      if (p?.status === "ok") return Math.max(p.major, 0);
      return list;
    },
    [codeTrimmed, previewUserId, previewByProductId, isAnnual],
  );

  const onSubmit = useCallback(
    (data: MembershipData) => {
      const code = discountCode.trim();

      if (code && !previewUserId?.trim()) {
        setDiscountError(t("membership.signInRequiredApply"));
        return;
      }
      if (membershipSelectionSaving) {
        setDiscountError("Saving your membership selection… please wait.");
        return;
      }

      const selectedMembership = memberships.find(
        (m) => m.name === data.membershipName,
      );

      if (code && previewUserId?.trim()) {
        if (previewRunning) {
          setDiscountError(t("membership.checkingPromoWait"));
          return;
        }
        const pid = selectedMembership
          ? productIdFor(selectedMembership, data.isAnnualMembership === 1)
          : "";
        const entry = pid ? previewByProductId[pid] : undefined;
        if (!entry || entry.status === "loading") {
          setDiscountError(t("membership.stillValidatingPromo"));
          return;
        }
        if (entry.status === "error") {
          setDiscountError(
            entry.message || t("membership.invalidDiscountCode"),
          );
          return;
        }
        if (entry.status !== "ok") {
          setDiscountError(t("membership.couldNotValidatePromo"));
          return;
        }
      }

      const advance = () => {
        if (!selectedMembership) return;
        const price = displayedMajor(selectedMembership) as number;
        setMembershipPrice(price);
        onNext({ ...data, isAnnualMembership }, price);
      };

      if (
        syncMembershipSelection &&
        previewUserId?.trim() &&
        data.membershipName?.trim()
      ) {
        setMembershipSelectionSaving(true);
        void syncRegistrationMembership({
          objectId: previewUserId.trim(),
          membershipName: data.membershipName,
          membershipId:
            selectedMembership?.id && selectedMembership.id > 0
              ? selectedMembership.id
              : undefined,
          isAnnualMembership: data.isAnnualMembership,
        })
          .then(() => {
            setMembershipSelectionSaving(false);
            advance();
          })
          .catch(() => {
            setMembershipSelectionSaving(false);
            setDiscountError(
              "Could not save membership selection. Please try again.",
            );
          });
        return;
      }

      advance();
    },
    [
      discountCode,
      previewUserId,
      previewRunning,
      memberships,
      previewByProductId,
      displayedMajor,
      setMembershipPrice,
      onNext,
      setDiscountError,
      syncMembershipSelection,
      membershipSelectionSaving,
      isAnnualMembership,
      t,
    ],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-100">
      <div className="d-flex flex-column gap-3" style={CONTAINER_STYLE}>
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
          <h5 className="mb-0">{t("membership.selectMembership")}</h5>
          <div
            className="d-flex align-items-center gap-2"
            role="group"
            aria-label={t("membership.monthlyAnnual")}
          >
            <span
              className={`small ${isAnnual ? "text-muted" : "fw-semibold"}`}
            >
              {t("checkout.monthly")}
            </span>
            <div className="form-check form-switch m-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="membership-billing-cycle-toggle"
                checked={isAnnual}
                onChange={(e) => setBillingCycle(e.target.checked)}
                aria-label={t("membership.monthlyAnnual")}
              />
            </div>
            <span
              className={`small ${isAnnual ? "fw-semibold" : "text-muted"}`}
            >
              {t("checkout.annual")}
            </span>
          </div>
        </div>

        {sortedMemberships.map((m) => {
          const list = listPriceMajor(m, isAnnual);
          const shown = displayedMajor(m);
          const pid = productIdFor(m, isAnnual);
          const preview = pid ? previewByProductId[pid] : undefined;
          const hasOkStripe = Boolean(
            codeTrimmed && previewUserId?.trim() && preview?.status === "ok",
          );
          const isCurrentPlan =
            !!currentMembershipName &&
            isSameMembershipPlan(m.name, currentMembershipName);
          const isLockedOut =
            !!lockedMembershipName &&
            !isSameMembershipPlan(m.name, lockedMembershipName);
          const isUnavailableAnnual =
            isAnnual && isEarlyStageMembership(m.name);
          const isNotSelectable =
            isCurrentPlan || isLockedOut || isUnavailableAnnual;
          const isSelected = membershipName === m.name && !isUnavailableAnnual;

          return (
            <div key={m.name} className="d-flex flex-column">
              <div
                className={`d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center p-3 rounded-3 border ${
                  isSelected
                    ? "bg-light fw-bold border-primary"
                    : "border-secondary"
                } ${isNotSelectable ? "opacity-75" : ""}`}
                style={
                  isNotSelectable
                    ? { cursor: "not-allowed", pointerEvents: "none" }
                    : CARD_CLICKABLE_STYLE
                }
                onClick={
                  isNotSelectable
                    ? undefined
                    : () => setValue("membershipName", m.name)
                }
              >
                <div className="d-flex align-items-center gap-3 min-w-0">
                  <input
                    type="radio"
                    name="membershipName"
                    checked={isSelected}
                    disabled={isNotSelectable}
                    onChange={() =>
                      !isNotSelectable && setValue("membershipName", m.name)
                    }
                  />
                  <div className="d-flex align-items-center gap-2 flex-wrap min-w-0">
                    <span>
                      {(() => {
                        const key = membershipPlanLabelKey(m.name);
                        return key
                          ? t(key)
                          : m.name.replace("Membership", "").trim();
                      })()}
                    </span>
                    {isCurrentPlan && (
                      <span className="badge bg-secondary text-nowrap">
                        {t("membership.currentPlan")}
                      </span>
                    )}
                    {isUnavailableAnnual && (
                      <span className="badge bg-secondary text-nowrap">
                        {t("membership.notAvailableAnnual")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-end text-md-end mt-2 mt-md-0">
                  {!isUnavailableAnnual && (
                    <span className="text-nowrap">
                      {preview?.status === "loading" &&
                        codeTrimmed &&
                        previewUserId?.trim() && (
                          <span className="text-muted small me-2">…</span>
                        )}
                      {hasOkStripe && shown < list - 1e-9 && (
                        <span style={STRIKETHROUGH_STYLE}>
                          £{list.toFixed(2)}
                        </span>
                      )}
                      £{Math.max(Number(shown), 0).toFixed(2)}/
                      {isAnnual
                        ? t("membership.perYear")
                        : t("membership.perMonth")}
                    </span>
                  )}
                </div>
              </div>
              {isEarlyStageMembership(m.name) &&
                membershipName === m.name &&
                !isAnnual &&
                onEarlyStageYoungProfessional &&
                showEarlyStageYoungProfessionalLink && (
                <div className="text-end mt-2">
                  <button
                    type="button"
                    className="btn btn-link p-0 border-0 bg-transparent text-primary text-decoration-underline membership-young-pro-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEarlyStageYoungProfessional(membershipName);
                    }}
                  >
                    {t("membership.earlyStage.youngProPrompt")}{" "}
                    {t("membership.earlyStage.under23Link")}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {errors.membershipName && (
          <p className="text-danger mb-0 px-1">
            {errors.membershipName.message}
          </p>
        )}
      </div>

      <div className="mt-4">
        {!showDiscountInput ? (
          <button
            type="button"
            className="btn btn-link p-0 text-start border-0 bg-transparent text-primary text-decoration-underline"
            onClick={() => setShowDiscountInput(true)}
          >
            {t("membership.haveDiscountCode")}
          </button>
        ) : (
          <Form.Group>
            <Form.Label>{t("membership.discountCode")}</Form.Label>
            <Form.Control
              type="text"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder={t("membership.enterDiscountCode")}
              value={discountCode}
              onChange={handleDiscountChange}
            />
            {codeTrimmed && !previewUserId?.trim() && (
              <Form.Text className="text-warning d-block">
                {t("membership.signInRequiredPromo")}
              </Form.Text>
            )}
            {codeTrimmed && previewUserId?.trim() && previewRunning && (
              <Form.Text className="text-muted d-block">
                {t("membership.checkingPromoCode")}
              </Form.Text>
            )}
            {membershipStripeFieldError && (
              <Form.Text className="text-danger d-block">
                {membershipStripeFieldError}
              </Form.Text>
            )}
            {discountError && (
              <Form.Text className="text-danger d-block">
                {discountError}
              </Form.Text>
            )}
          </Form.Group>
        )}
      </div>

      <div className="d-flex justify-content-between mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onBack}
        >
          {t("auth.back")}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={Boolean(
            membershipSelectionSaving ||
            (codeTrimmed &&
              previewUserId?.trim() &&
              (previewRunning || Boolean(membershipStripeFieldError))),
          )}
        >
          {t("membership.next")}
        </button>
      </div>
    </form>
  );
}

MembershipStepInner.displayName = "MembershipStep";

const MembershipStep = memo(MembershipStepInner);
MembershipStep.displayName = "MembershipStep";

export default MembershipStep;
